import assert from "node:assert";
import { FirebaseError, FirebaseOptions, initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  initializeAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  User,
} from "firebase/auth";
import {
  createContext,
  createEffect,
  createResource,
  createSignal,
  onCleanup,
  ParentComponent,
  useContext,
} from "solid-js";

type SignInParams = {
  email: string;
  password: string;
  storeCredentials?: boolean;
};

type SignUpParams = {
  email: string;
  password: string;
  storeCredentials?: boolean;
};

type AuthState = {
  readonly currentUser?: User;
  readonly ready: Promise<void>;
  signOut(): Promise<boolean>;
  signIn(params: SignInParams): Promise<User>;
  signUp(params: SignUpParams): Promise<User>;
  resetPassword(email: string, redirectUrl: string): Promise<void>;
  resendVerificationEmail(
    redirectUrl: URL
  ): Promise<{ email: string; verified: boolean }>;
};
const authContext = createContext<AuthState>();

export const AuthProvider: ParentComponent<{
  config: FirebaseOptions;
  publicURL: URL;
}> = (props) => {
  const appName = `onesign`;
  const firebaseApp = initializeApp(props.config, appName);
  const firebaseAuth = initializeAuth(firebaseApp, {
    persistence: [browserLocalPersistence, browserSessionPersistence],
  });
  const initialUser = new Promise<User | undefined>((resolve) => {
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      resolve(user ?? undefined);
      unsubscribe();
    });
  });
  const [user, setUser] = createSignal<{ user: User | undefined } | false>(
    false
  );
  createEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      setUser({ user: user ?? undefined });
    });
    onCleanup(unsubscribe);
  });

  const [state, { mutate, refetch }] = createResource<
    { user: User | undefined },
    { user: User | undefined }
  >(user, async (source, info) => {
    return { user: source?.user };
  });

  const authState: AuthState = {
    get currentUser() {
      return state.latest?.user;
    },
    get ready() {
      return initialUser.then(() => {});
    },
    async signOut() {
      await initialUser;
      if (firebaseAuth.currentUser == null) {
        return false;
      }
      await firebaseAuth.signOut();
      return true;
    },
    async signIn(params: SignInParams) {
      await firebaseAuth.setPersistence(
        params.storeCredentials
          ? browserLocalPersistence
          : browserSessionPersistence
      );
      const { user } = await signInWithEmailAndPassword(
        firebaseAuth,
        params.email,
        params.password
      );
      return user;
    },
    async signUp(params: SignUpParams) {
      await firebaseAuth.setPersistence(
        params.storeCredentials
          ? browserLocalPersistence
          : browserSessionPersistence
      );
      const { user } = await createUserWithEmailAndPassword(
        firebaseAuth,
        params.email,
        params.password
      );
      await sendEmailVerification(user, {
        url: new URL("/users/verify-email", props.publicURL).href,
      });
      return user;
    },
    async resetPassword(email: string, redirectUrl: string) {
      await sendPasswordResetEmail(firebaseAuth, email, {
        url: new URL(redirectUrl, props.publicURL).href,
      });
    },
    async resendVerificationEmail(redirectUrl: URL) {
      const user = firebaseAuth.currentUser;
      if (user == null) {
        return { email: "", verified: false };
      }
      if (user.emailVerified) {
        assert(user.email != null);
        return { email: user.email, verified: true };
      }
      await sendEmailVerification(user, {
        url: redirectUrl.href,
      });
      assert(user.email != null);
      return { email: user.email, verified: false };
    },
  };
  return (
    <authContext.Provider value={authState}>
      {props.children}
    </authContext.Provider>
  );
};

export const useAuth = () => {
  const auth = useContext(authContext);
  if (auth) {
    return auth;
  }
  throw new Error("not in auth context");
};

export const isWrongPassword = (err: unknown) =>
  err instanceof FirebaseError && err.code === "auth/wrong-password";
export const isUserNotFound = (err: unknown) =>
  err instanceof FirebaseError && err.code === "auth/user-not-found";
export const isInvalidCredentials = (err: unknown) =>
  isWrongPassword(err) || isUserNotFound(err);
export const isTooManyRequests = (err: unknown) =>
  err instanceof FirebaseError && err.code == "auth/too-many-requests";
export const isInvalidEmail = (err: unknown) =>
  err instanceof FirebaseError && err.code == "auth/invalid-email";
